import { css } from "@emotion/react";
import { FC, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Info, LockOpen } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Heading,
  Icon,
  Modal,
  ModalHeader,
  ModalPortal,
  SpacingToken,
  Switch,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { emailTemplatesUtm } from "constants/searchParams";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { useToggle } from "hooks/use-toggle";
import { LicenseActions, selectHasPoweredByFooter } from "store/entities/license";
import { Plan, selectCurrentSubscriptionPlan } from "store/entities/subscriptions";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

interface IProps {
  isVisualBuilder: boolean;
}

export const HideHelpDeskSwitch: FC<IProps> = ({ isVisualBuilder }) => {
  const isEmbeddedInIframe = isInIframe();

  const hasPoweredByFooter = useSelector(selectHasPoweredByFooter);
  const [isUpsellModalOpen, toggleUpsellModal] = useToggle(false);
  const currentSubscriptionPlan = useSelector(selectCurrentSubscriptionPlan);
  const isTeamOrBusiness = currentSubscriptionPlan === Plan.Team || currentSubscriptionPlan === Plan.Business;
  const switchRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (switchRef.current) {
      switchRef.current.disabled = false;
    }
  }, []);

  const togglePoweredByFooter = useCallback(() => {
    trackEngagement(
      `${isVisualBuilder ? "Visual" : "HTML"} template editor: Footer show footer pressed`,
      EventPlace.Settings,
      { previousValue: hasPoweredByFooter }
    );
    dispatch(
      LicenseActions.setPoweredByFooter({ poweredByFooter: !hasPoweredByFooter, requestSource: "email-template" })
    );
  }, [dispatch, hasPoweredByFooter, isVisualBuilder]);

  if (isEmbeddedInIframe) {
    return null;
  }

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <Flex width="151px" justifyContent="space-between">
          <Text>Hide HelpDesk label</Text>
          <Tooltip placement="top" triggerRenderer={() => <Icon source={Info} />} theme="invert">
            <span>Hide the &ldquo;This email is provided by HelpDesk&ldquo; label in emails.</span>
          </Tooltip>
        </Flex>
        <Switch
          state={isTeamOrBusiness ? "locked" : "regular"}
          on={!hasPoweredByFooter}
          innerRef={switchRef}
          onChange={() => {
            if (isTeamOrBusiness) {
              toggleUpsellModal();
              trackEngagement(
                `${isVisualBuilder ? "Visual" : "HTML"} template editor: Whitelabel upgrade modal shown`,
                EventPlace.Settings
              );
            } else {
              togglePoweredByFooter();
            }
          }}
        />
      </Flex>
      {isUpsellModalOpen && (
        <ModalPortal zIndex={StackingContextLevel.Modal}>
          <Modal
            css={css({ maxWidth: "480px" })}
            onClose={toggleUpsellModal}
            heading={<ModalHeader />}
            footer={
              <Flex justifyContent="center" width="100%">
                <Button
                  href={`/subscription${emailTemplatesUtm}`}
                  kind="primary"
                  target="_blank"
                  onClick={() =>
                    trackEngagement(
                      `${isVisualBuilder ? "Visual" : "HTML"} template editor: Whitelabel upgrade button pressed`,
                      EventPlace.Settings
                    )
                  }
                >
                  Upgrade to Enterprise
                </Button>
              </Flex>
            }
          >
            <Flex column alignItems="center" gap={withVar(SpacingToken.Spacing2)}>
              <Icon
                size="xxlarge"
                source={LockOpen}
                customColor={withVar(DesignToken.ContentBasicDisabled)}
                css={{ marginBottom: withVar(SpacingToken.Spacing1) }}
              />
              <Heading>Upgrade to hide HelpDesk label</Heading>
              <Text textAlign="center">
                Hiding the HelpDesk label in your emails is only possible in the Enterprise solution.
              </Text>
            </Flex>
          </Modal>
        </ModalPortal>
      )}
    </>
  );
};
